import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import AppManager from "../../managers/AppManager";
import RouteManager from "../../managers/RouteManager";
import {
    DKLabel,
    DKButton,
    DKIcons,
    DKDateRangePicker,
    DKInput,
    INPUT_VIEW_DIRECTION,
    INPUT_TYPE,
    showLoader,
    removeLoader
} from "deskera-ui-library";
import DataGridHolderPayroll from "../common/DataGridHolderPayroll";
import { TABLES, TABLE_DISPLAY_NAME } from "../../managers/TableManger";
import moment from "moment";
import Utility, { deepClone } from "../../utility/Utility";
import { DATE_FORMAT } from "../../constants/Constant";
import AuditLogUtils, { AUDIT_LOG_COLUMNS, AUDIT_LOG_MODULES } from "./AuditLogUtils";
import EmployeeListService from "../../services/EmployeeListService";
import AuditLogService from "../../services/AuditLogService";
import { TENANT, TenantService } from "../../managers/TenantManagerService";
import { COUNTRY } from "../../constants/CountryConstant";
import ExpenseService from "../../services/ExpenseService";

function AuditLogs(props) {
    const history = useHistory();
    const { t } = useTranslation();

    const [auditLogs, setAuditLogs] = useState({
        columnData: AUDIT_LOG_COLUMNS,
        rowData: [],
        filter: [],
        originalData: []
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPageCount, setTotalPageCount] = useState(0);

    const [users, setUsers] = useState(null);
    const [userNames, setUserNames] = useState(null);
    const [showDateRangePicker, setShowDateRangePicker] = useState(false);
    const [fromDate, setFromDate] = useState(moment().startOf("month").startOf("day"));
    const [toDate, setToDate] = useState(moment().endOf("month").endOf("day"));
    const [module, setModule] = useState(AUDIT_LOG_MODULES[0]);
    const [groups, setGroups] = useState(null);

    const [userPopUpData, setUserIdPopup] = useState({
        enable: false,
        users: []
    });

    useEffect(() => {
        RouteManager.setPresenter({ props: { ...props, history: history } });
        AppManager.scrollToTop();

        AuditLogService.API_CONFIG = {
            currentPage: 1,
            limit: 20,
            fromDate: fromDate,
            toDate: toDate
        };

        populateCountrySpecificModules();
        fetchDependancies();
        window.openUsersList = (iamUserIds) => {
            setUserIdPopup({
                enable: true,
                users: Utility.decodeJSON(iamUserIds).split(',')
            })
        }
        return () => {
            window.openUsersList = null;
        }
    }, []);

    useEffect(() => {
        if (users && userNames && groups) {
            getAuditLogs();
        }
    }, [users, userNames, groups, module, currentPage, fromDate, toDate]);

    const populateCountrySpecificModules = () => {
        const country = TenantService.get(TENANT.COUNTRY_CODE);
        switch (country) {
            case COUNTRY.SG:
                const CPF_ACCOUNT = {
                    label: "CPF Account",
                    value: "account",
                    fieldsToShow: {
                        Name: "compliance.name",
                        "Submission Number": "compliance.submissionNumber",
                        "ID Number": "compliance.organizationIdNumber"
                    }
                };
                AuditLogUtils.pushModule(CPF_ACCOUNT);
                break;
            case COUNTRY.MY:
                const EPF_ACCOUNT = {
                    label: "EPF Account",
                    value: "account",
                    fieldsToShow: {
                        Name: "compliance.epfAccountName",
                        Code: "compliance.epfCode",
                        "EPF Number": "compliance.empEpf"
                    }
                };
                AuditLogUtils.pushModule(EPF_ACCOUNT);
                break;
            default:
                break;
        }
    }

    const fetchDependancies = async () => {
        const params = {
            page: 0,
            limit: 1000,
            sort: "email",
            sortDir: "desc"
        };

        const dependancies = [EmployeeListService.getUsersShortInfo(params), ExpenseService.fetchExpensesGroupNoLimit()];
        const response = await Promise.all(dependancies);

        const usersData = response[0];
        const groupData = response[1];

        if (usersData?.content?.length > 0) {
            const usersObject = {};
            const userNames = {};
            usersData.content.forEach((user) => {
                const userName = `${user.firstName} ${user.lastName}`;
                usersObject[user.iamUserId] = userName;
                userNames[user.id] = userName;
            });

            setUsers(usersObject);
            setUserNames(userNames);
        } else {
            setUsers({});
            setUserNames({});
        }

        if (groupData?.content?.length > 0) {
            const groups = {};
            groupData.content.forEach((group) => {
                groups[group.id] = group.name;
            });

            setGroups(groups);
        } else {
            setGroups({});
        }
    }

    const getAuditLogs = () => {
        AuditLogService.API_CONFIG = {
            ...AuditLogService.API_CONFIG,
            module: module?.value,
            currentPage: currentPage,
            fromDate: fromDate.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
            toDate: toDate.format("YYYY-MM-DDTHH:mm:ss.SSSZ")
        }

        showLoader();
        AuditLogService.getAuditLogs().then(
            (response) => {
                removeLoader();
                if (Array.isArray(response?.data)) {
                    const rowData = AuditLogUtils.getAuditLogRowData(response.data, users, userNames, groups);
                    setAuditLogs({
                        ...auditLogs,
                        rowData: rowData,
                        originalData: rowData
                    });
                    setTotalPageCount(response?.filterConfig?.totalPage);
                } else {
                    setAuditLogs({
                        ...auditLogs,
                        rowData: [],
                        originalData: []
                    });
                    setTotalPageCount(1);
                }
            }, (error) => {
                setAuditLogs({
                    ...auditLogs,
                    rowData: [],
                    originalData: []
                });
                setTotalPageCount(1);
                removeLoader();
            }
        );
    }

    const getDatePickerTitle = () => {
        return `${Utility.formatDate(fromDate.format(DATE_FORMAT.IND), DATE_FORMAT.IND)} - ${Utility.formatDate(toDate.format(DATE_FORMAT.IND), DATE_FORMAT.IND)}`;
    }

    const renderDateRangePicker = () => {
        return (
            <div className="position-absolute bg-white z-index-6 rounded" style={{ right: 24, top: 66 }}>
                <DKDateRangePicker
                    className="border shadow "
                    color={'rgb(0, 102, 65)'}
                    startDate={fromDate.toDate()}
                    selectedStartDate={fromDate.toDate()}
                    selectedEndDate={toDate.toDate()}
                    onSelectDateRange={(startDate, endDate) => {
                        if (startDate && endDate) {
                            setFromDate(moment(startDate).startOf("day"));
                            setToDate(moment(endDate).endOf("day"));
                            setShowDateRangePicker(false);
                            setCurrentPage(1);
                        }
                    }}
                    onClose={() => {
                        setShowDateRangePicker(false);
                    }}
                />
            </div>
        );
    }
    const onUserIdPopClose = () => {
        setUserIdPopup(false);
    }

    return (
        <div className="parent-width mb-xl">
            {showDateRangePicker && renderDateRangePicker()}
            <div className="row justify-content-between">
                <DKLabel text={TABLE_DISPLAY_NAME[TABLES.PEOPLE_AUDIT_LOGS]} className="fw-m fs-m" />
                <div className="d-flex">
                    <DKButton
                        className="bg-white mr-r position-relative"
                        title={getDatePickerTitle()}
                        icon={DKIcons.ic_calendar}
                        onClick={() => {
                            setShowDateRangePicker(true);
                        }}
                    />
                    <div style={{ width: 200 }}>
                        <DKInput
                            type={INPUT_TYPE.DROPDOWN}
                            direction={INPUT_VIEW_DIRECTION.VERTICAL}
                            displayKey={"label"}
                            dropdownConfig={{
                                allowSearch: AUDIT_LOG_MODULES.length > 7,
                                data: AUDIT_LOG_MODULES,
                                renderer: (i, item) => item.label,
                                onSelect: (i, item) => {
                                    setModule(item);
                                    setCurrentPage(1);
                                }
                            }}
                            value={module}
                            onChange={() => { }}
                        />
                    </div>
                </div>
            </div>
            <DataGridHolderPayroll
                isHeaderHidden={true}
                tableName={TABLES.PEOPLE_AUDIT_LOGS}
                displayTableName={TABLE_DISPLAY_NAME[TABLES.PEOPLE_AUDIT_LOGS]}
                allowBulkOperation={false}
                allowColumnEdit={false}
                allowColumnDelete={false}
                allowColumnShift={false}
                allowSearch={false}
                allowFilter={false}
                allowColumnAdd={false}
                allowRowAdd={false}
                allowColumnSort={false}
                refresh={false}
                allowDataExport={false}
                allowRowEdit={false}
                showContextEdit={false}
                isActionEnabled={false}
                gridData={auditLogs}
                currentPage={currentPage}
                totalPageCount={totalPageCount}
                onPaginationClick={(requestedPageNumber) => {
                    setCurrentPage(requestedPageNumber);
                }}
                addButtonConfig={[]}
            />
            { userPopUpData.enable && (
                <>
                    <div className=" transparent-background">
                        <div className="p-h-r pt-r pb-m popup-window custom-popup-outer" style={{ padding: 0, maxWidth: "25%", width: "100%", overflow: "hidden" }} >
                            
                                <div className="row justify-content-between p-h-m p-v-s bg-gray2">
                                    <DKLabel text={"Employees"} className="fs-l fw-m" />
                                    <DKButton icon={DKIcons.ic_close} onClick={() => onUserIdPopClose(false)} />
                                </div>
                                <div
                                    className=" parent-width p-m custom-popup-body"
                                >
                                    {userPopUpData.users.map((i, index) => {
                                        return (
                                            <>
                                                <div className="pb-s">
                                                    <div className="row fs-m">
                                                        <div>{`${index + 1}. ${i} `}</div>
                                                    </div>
                                                </div>
                                            </>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>

                </>
            )}
        </div>
    );
}

export default AuditLogs;
